import { useState, useEffect, useRef } from 'react';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'barrack-spawn' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'barrack-spawn' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'barrack-spawn',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  'CONNECTION_FAILED': 'Unable to connect to data service',
  'CONNECTION_LOST': 'Connection to data service lost',
  'DATA_PARSE_ERROR': 'Unable to process server data',
  'INVALID_DATA': 'Invalid data received from server',
  'RECONNECT_FAILED': 'Failed to reconnect after multiple attempts',
  'WEBSOCKET_ERROR': 'Data service error occurred',
      'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',

  'RATE_LIMITED': 'Too many connection attempts. Please wait.',
  'VALIDATION_ERROR': 'Data validation failed',
  'NETWORK_ERROR': 'Network connection error'
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['WEBSOCKET_ERROR'];
  }
   if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  // Check backend error code first (in response data)
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  // Check direct error code 
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['CONNECTION_FAILED']; // or add 'SESSION_EXPIRED' to ERROR_MESSAGES
  }
  
  // Handle network/connection specific status codes
  if (error.response?.status >= 500 || error.status >= 500) {
    return ERROR_MESSAGES['CONNECTION_FAILED'];
  }
  
  if (error.response?.status === 429 || error.status === 429) {
    return ERROR_MESSAGES['RATE_LIMITED'];
  }
  
  // Fallback to sanitized message if available
  if (error.message && typeof error.message === 'string') {
    return sanitizeText(error.message);
  }
  
  return ERROR_MESSAGES['WEBSOCKET_ERROR'];
};
// Security: Data validation functions
const validateStockData = (data) => {
  if (!data || !Array.isArray(data)) {
    return { valid: false, error: 'Invalid stock data format' };
  }
  
  for (const item of data) {
    if (!item || typeof item !== 'object') {
      return { valid: false, error: 'Invalid stock item format' };
    }
    
    if (!item.region || typeof item.region !== 'string') {
      return { valid: false, error: 'Invalid region data' };
    }
    
    if (item.models && !Array.isArray(item.models)) {
      return { valid: false, error: 'Invalid models data' };
    }
  }
  
  return { valid: true };
};

const validateFlavorData = (data) => {
  if (!data || !Array.isArray(data)) {
    return { valid: false, error: 'Invalid flavor data format' };
  }
  
  for (const item of data) {
    if (!item || typeof item !== 'object') {
      return { valid: false, error: 'Invalid flavor item format' };
    }
  }
  
  return { valid: true };
};

// Security: WebSocket message validation
const validateWebSocketMessage = (rawMessage) => {
  const startTime = performance.now();
  
  try {
    if (!rawMessage || typeof rawMessage !== 'string') {
      return { valid: false, error: 'Invalid message format' };
    }
    
    if (rawMessage.length > 50000) { // 50KB limit
      return { valid: false, error: 'Message too large' };
    }
    
    let parsed;
    try {
      parsed = JSON.parse(rawMessage);
    } catch (parseError) {
      return { valid: false, error: 'Invalid JSON format' };
    }
    
    if (!parsed || typeof parsed !== 'object') {
      return { valid: false, error: 'Invalid message structure' };
    }
    
    if (!parsed.type || typeof parsed.type !== 'string') {
      return { valid: false, error: 'Missing message type' };
    }
    
    // Sanitize message type
    const sanitizedType = sanitizeText(parsed.type);
    if (!['stocks', 'flavors', 'pong', 'error', 'refresh'].includes(sanitizedType)) {
      return { valid: false, error: 'Invalid message type' };
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('validate_websocket_message', duration, {
      messageType: sanitizedType,
      messageSize: rawMessage.length
    });
    
    return { 
      valid: true, 
      message: {
        ...parsed,
        type: sanitizedType
      }
    };
    
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('validate_websocket_message_error', duration, {
      errorName: error.name
    });
    
    logError('WebSocket message validation failed', {
      messageLength: rawMessage?.length || 0
    }, error);
    
    return { valid: false, error: 'Message validation failed' };
  }
};

// Performance: Connection metrics
const ConnectionMetrics = {
  totalConnections: 0,
  successfulConnections: 0,
  failedConnections: 0,
  totalReconnections: 0,
  averageConnectionTime: 0,
  messagesReceived: 0,
  messagesProcessed: 0,
  dataValidationFailures: 0,
  
  recordConnection(duration, success = true) {
    this.totalConnections++;
    if (success) {
      this.successfulConnections++;
      this.averageConnectionTime = 
        (this.averageConnectionTime * (this.successfulConnections - 1) + duration) / 
        this.successfulConnections;
    } else {
      this.failedConnections++;
    }
  },
  
  recordReconnection() {
    this.totalReconnections++;
  },
  
  recordMessage(processed = true) {
    this.messagesReceived++;
    if (processed) {
      this.messagesProcessed++;
    }
  },
  
  recordValidationFailure() {
    this.dataValidationFailures++;
  },
  
  getMetrics() {
    return {
      ...this,
      successRate: this.totalConnections > 0 ? 
        (this.successfulConnections / this.totalConnections * 100).toFixed(2) + '%' : 
        '0%',
      processingRate: this.messagesReceived > 0 ?
        (this.messagesProcessed / this.messagesReceived * 100).toFixed(2) + '%' :
        '0%'
    };
  }
};

// Security: Rate limiting for reconnections
const ReconnectionRateLimit = {
  attempts: [],
  maxAttempts: 10,
  windowMs: 5 * 60 * 1000, // 5 minutes
  
  canAttempt() {
    const now = Date.now();
    this.attempts = this.attempts.filter(time => now - time < this.windowMs);
    
    if (this.attempts.length >= this.maxAttempts) {
      logError('Reconnection rate limit exceeded', {
        attempts: this.attempts.length,
        maxAttempts: this.maxAttempts,
        windowMs: this.windowMs
      });
      return false;
    }
    
    return true;
  },
  
  recordAttempt() {
    this.attempts.push(Date.now());
  },
  
  reset() {
    this.attempts = [];
  }
};

export function Utility() {
  const [stocks, setStocks] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Refs for managing reconnection and performance
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const connectionStartTimeRef = useRef(null);
  const componentMountTime = useRef(Date.now());
  const maxReconnectAttempts = 5;
  
  // Set Sentry context
  useEffect(() => {
    Sentry.setContext("barrack-spawn", {
      wsConnected,
      hasError: !!error,
      stocksCount: stocks.length,
      flavorsCount: flavors.length,
      lastUpdated,
      reconnectAttempts: reconnectAttemptsRef.current
    });
  }, [wsConnected, error, stocks.length, flavors.length, lastUpdated]);

  // Component mount logging
  useEffect(() => {
    logInfo('Utility component mounted', {
      timestamp: new Date().toISOString()
    });
    
    logUserAction('barrack_spawn_initialized');
  }, []);
  
  useEffect(() => {
    // Function to set up ping interval with performance monitoring
    const startPingInterval = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      pingIntervalRef.current = setInterval(() => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          try {
            const pingMessage = JSON.stringify({ 
              type: 'ping', 
              timestamp: new Date().toISOString() 
            });
            socketRef.current.send(pingMessage);
            
            logInfo('Ping sent to server', {
              timestamp: new Date().toISOString()
            });
          } catch (error) {
            logError('Failed to send ping', {}, error);
          }
        }
      }, 30000);
    };
    
    const stopPingInterval = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    
    // Enhanced reconnection with rate limiting and security
    const attemptReconnect = () => {
      const startTime = performance.now();
      
      if (!ReconnectionRateLimit.canAttempt()) {
        const safeError = {
          code: 'RATE_LIMITED',
          message: ERROR_MESSAGES.RATE_LIMITED
        };
        setError(getSafeErrorMessage(safeError));
        
        logUserAction('reconnection_rate_limited', {
          attempts: ReconnectionRateLimit.attempts.length,
          maxAttempts: ReconnectionRateLimit.maxAttempts
        });
        
        return;
      }
      
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        const safeError = {
          code: 'RECONNECT_FAILED',
          message: ERROR_MESSAGES.RECONNECT_FAILED
        };
        setError(getSafeErrorMessage(safeError));
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('reconnect_attempts_exhausted', duration, {
          maxAttempts: maxReconnectAttempts,
          totalTime: duration
        });
        
        logError('Maximum reconnection attempts reached', {
          attempts: reconnectAttemptsRef.current,
          maxAttempts: maxReconnectAttempts
        });
        
        return;
      }
      
      ReconnectionRateLimit.recordAttempt();
      reconnectAttemptsRef.current++;
      ConnectionMetrics.recordReconnection();
      
      const delay = Math.pow(2, reconnectAttemptsRef.current - 1) * 1000;
      
      logInfo('Scheduling reconnection attempt', {
        attempt: reconnectAttemptsRef.current,
        delay,
        maxAttempts: maxReconnectAttempts
      });
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      reconnectTimeoutRef.current = setTimeout(() => {
        logUserAction('websocket_reconnect_attempt', {
          attempt: reconnectAttemptsRef.current,
          delay
        });
        setupWebSocket();
      }, delay);
    };
    
    // Enhanced WebSocket setup with comprehensive monitoring
    const setupWebSocket = () => {
      const setupStartTime = performance.now();
      connectionStartTimeRef.current = setupStartTime;
      
      if (socketRef.current) {
        socketRef.current.close();
      }
      
      logInfo('Initiating WebSocket connection', {
        attempt: reconnectAttemptsRef.current + 1,
        timestamp: new Date().toISOString()
      });
      
      try {
const wsUrl = process.env.REACT_APP_WS_URL || 'wss://barrack.ai/ws';
const socket = new WebSocket(wsUrl);
        socketRef.current = socket;
        
        socket.addEventListener('open', (event) => {
          const connectionTime = performance.now() - connectionStartTimeRef.current;
          
          setWsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
          ReconnectionRateLimit.reset();
          
          ConnectionMetrics.recordConnection(connectionTime, true);
          
          logPerformance('websocket_connection_established', connectionTime, {
            previousAttempts: reconnectAttemptsRef.current
          });
          
          logInfo('WebSocket connection established', {
            connectionTime: `${connectionTime.toFixed(2)}ms`,
            timestamp: new Date().toISOString()
          });
          
          logUserAction('websocket_connected', {
            connectionTime: `${connectionTime.toFixed(2)}ms`,
            attempts: reconnectAttemptsRef.current + 1
          });
          
          startPingInterval();
        });
        
        socket.addEventListener('message', (event) => {
          const messageStartTime = performance.now();
          
          try {
            ConnectionMetrics.recordMessage();
            
            const validation = validateWebSocketMessage(event.data);
            if (!validation.valid) {
              ConnectionMetrics.recordValidationFailure();
              
              logError('WebSocket message validation failed', {
                error: validation.error,
                messageSize: event.data?.length || 0
              });
              
              return;
            }
            
            const message = validation.message;
            
            logInfo('WebSocket message received', {
              type: message.type,
              messageSize: event.data.length
            });
            
            if (message.type === 'stocks') {
              const processStartTime = performance.now();
              
              const stocksData = message.data?.stocks || message.data || [];
              const stockValidation = validateStockData(stocksData);
              
              if (!stockValidation.valid) {
                ConnectionMetrics.recordValidationFailure();
                
                logError('Stocks data validation failed', {
                  error: stockValidation.error,
                  dataLength: stocksData?.length || 0
                });
                
                return;
              }
              
              // Sanitize stocks data
              const sanitizedStocks = stocksData.map(stock => ({
                ...stock,
                region: sanitizeText(stock.region),
                models: stock.models?.map(model => ({
                  ...model,
                  model: sanitizeText(model.model),
                  available: sanitizeText(String(model.available))
                })) || []
              }));
              
              setStocks(sanitizedStocks);
              setLastUpdated(new Date().toISOString());
              
              const processTime = performance.now() - processStartTime;
              
              logPerformance('process_stocks_data', processTime, {
                stocksCount: sanitizedStocks.length,
                totalModels: sanitizedStocks.reduce((acc, stock) => acc + (stock.models?.length || 0), 0)
              });
              
              logUserAction('stocks_data_updated', {
                stocksCount: sanitizedStocks.length,
                processTime: `${processTime.toFixed(2)}ms`
              });
              
            } else if (message.type === 'flavors') {
              const processStartTime = performance.now();
              
              let flavorsList = [];
              
              if (message.data) {
                if (message.data.flavors && Array.isArray(message.data.flavors)) {
                  flavorsList = message.data.flavors;
                } else if (message.data.data && Array.isArray(message.data.data)) {
                  flavorsList = message.data.data;
                } else if (Array.isArray(message.data)) {
                  flavorsList = message.data;
                } else if (typeof message.data === 'object') {
                  if (message.data.status && message.data.message && message.data.data) {
                    flavorsList = message.data.data;
                  } else {
                    for (const key in message.data) {
                      if (Array.isArray(message.data[key])) {
                        flavorsList = message.data[key];
                        break;
                      }
                    }
                  }
                }
              }
              
              const flavorValidation = validateFlavorData(flavorsList);
              if (!flavorValidation.valid) {
                ConnectionMetrics.recordValidationFailure();
                
                logError('Flavors data validation failed', {
                  error: flavorValidation.error,
                  dataLength: flavorsList?.length || 0
                });
                
                if (flavorsList.length === 0) {
                  logInfo('Empty flavors list received, keeping existing data');
                  return;
                }
              }
              
              if (flavorsList.length > 0) {
                // Sanitize flavors data
                const sanitizedFlavors = flavorsList.map(flavor => {
                  const sanitized = {};
                  Object.keys(flavor).forEach(key => {
                    if (typeof flavor[key] === 'string') {
                      sanitized[key] = sanitizeText(flavor[key]);
                    } else {
                      sanitized[key] = flavor[key];
                    }
                  });
                  return sanitized;
                });
                
                setFlavors(sanitizedFlavors);
                setLastUpdated(new Date().toISOString());
                
                const processTime = performance.now() - processStartTime;
                
                logPerformance('process_flavors_data', processTime, {
                  flavorsCount: sanitizedFlavors.length
                });
                
                logUserAction('flavors_data_updated', {
                  flavorsCount: sanitizedFlavors.length,
                  processTime: `${processTime.toFixed(2)}ms`
                });
              }
            } else if (message.type === 'pong') {
              logInfo('Pong received from server', {
                timestamp: message.timestamp
              });
            }
            
            ConnectionMetrics.recordMessage(true);
            
            const messageProcessTime = performance.now() - messageStartTime;
            logPerformance('websocket_message_processed', messageProcessTime, {
              messageType: message.type,
              messageSize: event.data.length
            });
            
          } catch (err) {
            const messageProcessTime = performance.now() - messageStartTime;
            
            ConnectionMetrics.recordMessage(false);
            
            logPerformance('websocket_message_error', messageProcessTime, {
              errorName: err.name,
              messageSize: event.data?.length || 0
            });
            
            logError('Error processing WebSocket message', {
              messageSize: event.data?.length || 0,
              processTime: `${messageProcessTime.toFixed(2)}ms`
            }, err);
            
            const safeError = {
              code: 'DATA_PARSE_ERROR',
              message: ERROR_MESSAGES.DATA_PARSE_ERROR
            };
            setError(getSafeErrorMessage(safeError));
          }
        });
        
        socket.addEventListener('close', (event) => {
          const connectionDuration = performance.now() - connectionStartTimeRef.current;
          
          setWsConnected(false);
          stopPingInterval();
          
          logPerformance('websocket_connection_closed', connectionDuration, {
            code: event.code,
            reason: sanitizeText(event.reason),
            wasClean: event.wasClean
          });
          
          logInfo('WebSocket connection closed', {
            code: event.code,
            reason: sanitizeText(event.reason),
            wasClean: event.wasClean,
            connectionDuration: `${connectionDuration.toFixed(2)}ms`
          });
          
          logUserAction('websocket_disconnected', {
            code: event.code,
            wasClean: event.wasClean,
            connectionDuration: `${connectionDuration.toFixed(2)}ms`
          });
          
          if (!event.wasClean) {
            const safeError = {
              code: 'CONNECTION_LOST',
              message: ERROR_MESSAGES.CONNECTION_LOST
            };
            setError(getSafeErrorMessage(safeError));
            attemptReconnect();
          }
        });
        
        socket.addEventListener('error', (event) => {
          const connectionTime = performance.now() - connectionStartTimeRef.current;
          
          ConnectionMetrics.recordConnection(connectionTime, false);
          
          logPerformance('websocket_connection_error', connectionTime, {
            errorEvent: true
          });
          
          logError('WebSocket connection error', {
            connectionTime: `${connectionTime.toFixed(2)}ms`,
            readyState: socket.readyState
          });
          
          logUserAction('websocket_error', {
            connectionTime: `${connectionTime.toFixed(2)}ms`,
            readyState: socket.readyState
          });
          
          const safeError = {
            code: 'CONNECTION_FAILED',
            message: ERROR_MESSAGES.CONNECTION_FAILED
          };
          setError(getSafeErrorMessage(safeError));
          setWsConnected(false);
        });
        
      } catch (error) {
        const setupTime = performance.now() - setupStartTime;
        
        logPerformance('websocket_setup_error', setupTime, {
          errorName: error.name
        });
        
        logError('WebSocket setup failed', {
          setupTime: `${setupTime.toFixed(2)}ms`
        }, error);
        
        const safeError = {
          code: 'CONNECTION_FAILED',
          message: ERROR_MESSAGES.CONNECTION_FAILED
        };
        setError(getSafeErrorMessage(safeError));
      }
    };
    
    setupWebSocket();
    
    return () => {
      const componentLifetime = Date.now() - componentMountTime.current;
      
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      logPerformance('barrack_spawn_cleanup', componentLifetime, {
        connectionMetrics: ConnectionMetrics.getMetrics()
      });
      
      logInfo('Utility component unmounted', {
        lifetime: `${componentLifetime}ms`,
        finalStocksCount: stocks.length,
        finalFlavorsCount: flavors.length,
        wasConnected: wsConnected
      });
    };
  }, []);
  
  // Helper functions with enhanced validation and performance monitoring
  
  const getAvailableCountByModel = (modelName) => {
    const startTime = performance.now();
    
    try {
      if (!modelName || typeof modelName !== 'string') {
        return 0;
      }
      
      const sanitizedModelName = sanitizeText(modelName);
      let totalCount = 0;
      
      stocks.forEach(region => {
        if (region.models) {
          const model = region.models.find(m => m.model === sanitizedModelName);
          if (model && model.available) {
            if (typeof model.available === 'string' && model.available.endsWith('+')) {
              totalCount += parseInt(model.available.slice(0, -1)) || 0;
            } else {
              totalCount += parseInt(model.available) || 0;
            }
          }
        }
      });
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_count_by_model', duration, {
        modelName: sanitizedModelName,
        totalCount,
        regionsChecked: stocks.length
      });
      
      return totalCount;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_count_by_model_error', duration, {
        modelName: sanitizeText(modelName),
        errorName: error.name
      });
      
      logError('Error getting available count by model', {
        modelName: sanitizeText(modelName)
      }, error);
      
      return 0;
    }
  };
  
  const getRegionsForModel = (modelName) => {
    const startTime = performance.now();
    
    try {
      if (!modelName || typeof modelName !== 'string') {
        return [];
      }
      
      const sanitizedModelName = sanitizeText(modelName);
      const regions = [];
      
      stocks.forEach(region => {
        if (region.models && region.models.some(m => 
          m.model === sanitizedModelName && 
          m.available !== '0' && 
          m.available !== 0
        )) {
          regions.push(sanitizeText(region.region));
        }
      });
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_regions_for_model', duration, {
        modelName: sanitizedModelName,
        regionsFound: regions.length,
        totalRegionsChecked: stocks.length
      });
      
      return regions;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_regions_for_model_error', duration, {
        modelName: sanitizeText(modelName),
        errorName: error.name
      });
      
      logError('Error getting regions for model', {
        modelName: sanitizeText(modelName)
      }, error);
      
      return [];
    }
  };
  
  const getFlavorsForModel = (modelName) => {
    const startTime = performance.now();
    
    try {
      if (!modelName || typeof modelName !== 'string') {
        return [];
      }
      
      const sanitizedModelName = sanitizeText(modelName);
      const modelFlavors = flavors.filter(flavor => 
        flavor.gpu === sanitizedModelName
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_flavors_for_model', duration, {
        modelName: sanitizedModelName,
        flavorsFound: modelFlavors.length,
        totalFlavorsChecked: flavors.length
      });
      
      return modelFlavors;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_flavors_for_model_error', duration, {
        modelName: sanitizeText(modelName),
        errorName: error.name
      });
      
      logError('Error getting flavors for model', {
        modelName: sanitizeText(modelName)
      }, error);
      
      return [];
    }
  };
  
  const getAvailableGpuModels = () => {
    const startTime = performance.now();
    
    try {
      const models = new Set();
      
      stocks.forEach(region => {
        if (region.models) {
          region.models.forEach(model => {
            if (model.available && model.available !== '0') {
              models.add(sanitizeText(model.model));
            }
          });
        }
      });
      
      const modelArray = Array.from(models);
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_gpu_models', duration, {
        modelsFound: modelArray.length,
        regionsChecked: stocks.length
      });
      
      return modelArray;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_gpu_models_error', duration, {
        errorName: error.name
      });
      
      logError('Error getting available GPU models', {}, error);
      
      return [];
    }
  };
  
  const getAvailableCountsForRegion = (gpuModel, regionName) => {
    const startTime = performance.now();
    
    try {
      if (!stocks || !gpuModel || !regionName) {
        return [];
      }
      
      const sanitizedGpuModel = sanitizeText(gpuModel);
      const sanitizedRegionName = sanitizeText(regionName);
      
      const region = stocks.find(r => r.region === sanitizedRegionName);
      if (!region || !region.models) {
        return [];
      }
      
      const model = region.models.find(m => m.model === sanitizedGpuModel);
      if (!model || !model.configurations) {
        return [];
      }
      
      const counts = [];
      Object.entries(model.configurations).forEach(([config, count]) => {
        if (count > 0) {
          const countValue = parseInt(config.replace('x', ''), 10);
          if (!isNaN(countValue)) {
            counts.push({ value: countValue });
          }
        }
      });
      
      const sortedCounts = counts.sort((a, b) => a.value - b.value);
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_counts_for_region', duration, {
        gpuModel: sanitizedGpuModel,
        regionName: sanitizedRegionName,
        countsFound: sortedCounts.length
      });
      
      return sortedCounts;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('get_available_counts_for_region_error', duration, {
        gpuModel: sanitizeText(gpuModel),
        regionName: sanitizeText(regionName),
        errorName: error.name
      });
      
      logError('Error getting available counts for region', {
        gpuModel: sanitizeText(gpuModel),
        regionName: sanitizeText(regionName)
      }, error);
      
      return [];
    }
  };
  
  // Enhanced manual refresh with security and performance monitoring
  const refreshData = () => {
    const startTime = performance.now();
    
    try {
      logUserAction('manual_refresh_requested', {
        wsConnected,
        hasError: !!error,
        timestamp: new Date().toISOString()
      });
      
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        const refreshMessage = JSON.stringify({ type: 'refresh' });
        socketRef.current.send(refreshMessage);
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('manual_refresh_sent', duration, {
          connectionState: 'open'
        });
        
        logInfo('Manual refresh request sent to server', {
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else {
        reconnectAttemptsRef.current = 0;
        
        if (socketRef.current) {
          socketRef.current.close();
        }
        
        const safeError = {
          code: 'CONNECTION_LOST',
          message: ERROR_MESSAGES.CONNECTION_LOST
        };
        setError(getSafeErrorMessage(safeError));
        setWsConnected(false);
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('manual_refresh_reconnect_triggered', duration, {
          connectionState: socketRef.current?.readyState || 'null'
        });
        
        logUserAction('manual_refresh_triggered_reconnect', {
          connectionState: socketRef.current?.readyState || 'null',
          duration: `${duration.toFixed(2)}ms`
        });
      }
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('manual_refresh_error', duration, {
        errorName: error.name
      });
      
      logError('Manual refresh failed', {
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      const safeError = {
        code: 'WEBSOCKET_ERROR',
        message: ERROR_MESSAGES.WEBSOCKET_ERROR
      };
      setError(getSafeErrorMessage(safeError));
    }
  };
  
  return {
    stocks,
    flavors,
    wsConnected,
    error,
    lastUpdated,
    getAvailableCountByModel,
    getRegionsForModel,
    getFlavorsForModel,
    getAvailableGpuModels,
    getAvailableCountsForRegion,
    refreshData
  };
}

export default Utility;